import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';

interface FileWithPreview extends File {
  preview: string;
}

interface UploadDropzoneProps {
  files: FileWithPreview[];
  onFilesAdded: (files: File[]) => void;
  maxSize?: number;
  acceptedFormats?: string[];
}

const DropzoneContainer = styled.div<{ $isDragActive: boolean; $hasFiles: boolean }>`
  border: 2px dashed ${({ theme, $isDragActive, $hasFiles }) =>
    $isDragActive ? theme.colors.primary :
    $hasFiles ? theme.colors.success : theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing['2xl']};
  text-align: center;
  background-color: ${({ theme, $isDragActive }) =>
    $isDragActive ? theme.colors.primary + '10' : theme.colors.background};
  transition: all ${({ theme }) => theme.transitions.gallery.smooth};
  cursor: pointer;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => theme.colors.primary}05;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.mobile.lg};
    border-radius: ${({ theme }) => theme.borderRadius.gallery.comfortable};
    min-height: 160px;
    border-width: 3px;

    &:active {
      transform: scale(0.98);
    }
  }
`;

const DropzoneContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const DropzoneIcon = styled.div`
  font-size: 48px;
  color: ${({ theme }) => theme.colors.gray.dark};
`;

const DropzoneText = styled.p`
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  margin: 0;
`;

const DropzoneSubtext = styled.p`
  color: ${({ theme }) => theme.colors.gray.dark};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin: 0;
`;

const DEFAULT_MAX_SIZE = 10 * 1024 * 1024; // 10MB
const DEFAULT_ACCEPTED_FORMATS = ['.jpeg', '.jpg', '.png', '.gif', '.heic', '.heif', '.webp', '.dng', '.tiff', '.tif'];

export function UploadDropzone({
  files,
  onFilesAdded,
  maxSize = DEFAULT_MAX_SIZE,
  acceptedFormats = DEFAULT_ACCEPTED_FORMATS,
}: UploadDropzoneProps) {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    onFilesAdded(acceptedFiles);
  }, [onFilesAdded]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': acceptedFormats
    },
    maxSize,
  });

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  return (
    <DropzoneContainer
      {...getRootProps()}
      $isDragActive={isDragActive}
      $hasFiles={files.length > 0}
    >
      <input {...getInputProps()} />
      <DropzoneContent>
        <DropzoneIcon>
          {isDragActive ? '📤' : files.length > 0 ? '✅' : '🎨'}
        </DropzoneIcon>
        <DropzoneText>
          {isDragActive
            ? 'Drop the files here...'
            : files.length > 0
            ? `${files.length} file(s) selected`
            : isMobile
              ? 'Tap to select artwork files'
              : 'Drag & drop artwork files here, or click to select'}
        </DropzoneText>
        <DropzoneSubtext>
          Supports: JPG, PNG, GIF, HEIC, HEIF, WebP, DNG, TIFF (max 10MB each)
          {files.length > 0 && (
            <><br />Ready to upload {files.length} file{files.length !== 1 ? 's' : ''}</>
          )}
        </DropzoneSubtext>
      </DropzoneContent>
    </DropzoneContainer>
  );
}

export type { FileWithPreview, UploadDropzoneProps };
